<template>
  <div
    class="sticky top-16 z-40 w-full border-b border-gray-200/50 bg-white px-4 py-2.5 shadow-md"
    data-testid="qc-header"
  >
    <!--
      Prototype v3's one-row page head: full width, white, under the navbar, and
      STICKY — the BETA badge and the way back stay in sight on every state and
      at every scroll depth. Same surface as the evaluation page's sub-header
      (white, hairline bottom border, shadow-md).
      Back / up goes TOP-LEFT and stays quiet (CLAUDE.md, UI/UX defaults).
    -->
    <Link
      :href="route('evaluation.show', evaluationId)"
      class="mb-1 inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700"
      data-testid="qc-back-link"
    >
      <span class="pi pi-arrow-left text-xs" aria-hidden="true"></span>
      {{ $t('messages.evaluation.qualityCheck.back') }}
    </Link>

    <div class="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
      <h1 class="text-xl font-semibold text-gray-800">
        {{ $t('messages.evaluation.qualityCheck.title') }}
      </h1>
      <QcBetaBadge :support-email="supportEmail" class="self-center" />
      <p class="flex min-w-0 flex-wrap items-center gap-x-1.5 text-sm text-gray-400" data-testid="qc-header-meta">
        <template v-for="(item, index) in meta" :key="item.key">
          <span v-if="index > 0" class="text-gray-300" aria-hidden="true">·</span>
          <span class="break-words" :data-testid="item.testId">{{ item.text }}</span>
        </template>
        <span v-if="meta.length" class="text-gray-300" aria-hidden="true">·</span>
        <a
          :href="`mailto:${supportEmail}`"
          class="text-gray-500 underline hover:text-gray-700"
          data-testid="qc-beta-report-link"
        >{{ $t('messages.evaluation.qualityCheck.beta.report') }}</a>
      </p>
    </div>
  </div>
</template>

<script setup>
import { Link } from '@inertiajs/vue3'
import { format, parseISO } from 'date-fns'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import QcBetaBadge from './QcBetaBadge.vue'

/**
 * RED-351 rework — the screen's top bar (prototype v3, "6.4 one-row head"):
 * `Aruande kvaliteedikontroll` + BETA + one quiet line of context —
 * `Hindamine <nr> · <aadress> · kontrollitud <aeg> · anna veast teada`.
 */
const props = defineProps({
  evaluationId: { type: Number, required: true },
  reference: { type: String, default: null },
  address: { type: String, default: null },
  supportEmail: { type: String, required: true },
  /** ISO time the latest run finished, when it finished with a result. */
  checkedAt: { type: String, default: null },
})

const { t } = useI18n()

const meta = computed(() => {
  const items = []
  if (props.reference) {
    items.push({ key: 'reference', testId: 'qc-reference', text: t('messages.evaluation.qualityCheck.header.evaluation', { nr: props.reference }) })
  }
  if (props.address) {
    items.push({ key: 'address', testId: 'qc-address', text: props.address })
  }
  if (props.checkedAt) {
    const when = parseISO(props.checkedAt)
    items.push({
      key: 'checked',
      testId: 'qc-checked-at',
      text: t('messages.evaluation.qualityCheck.header.checkedAt', { date: format(when, 'dd.MM.yyyy'), time: format(when, 'HH:mm') }),
    })
  }
  return items
})
</script>
